import useApiRequest from "@/lib/hooks/use-request";
import { formatDateTime, formatPrice } from "@/lib/hooks/utils";
import { ProductResponseDto } from "@/lib/interfaces/product";
import PageNotFound from "@/page-components/error/page-not-found";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { Archive, ArchiveRestore, Edit2, X } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ProductEditor from "../product-editor";

const ProductItem = () => {
  const { id } = useParams();
  return <>{id ? <ProductItemFetcher id={id} /> : <PageNotFound />}</>;
};

const ProductItemFetcher = ({ id }: { id: string }) => {
  const [item, setItem] = useState<ProductResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<ProductResponseDto>(`settings/products/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setItem(response.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: ProductResponseDto) => <ProductDetails setReload={setReload} item={loadedItem} />}
    />
  );
};

const ProductDetails = ({
  item,
  setReload,
}: {
  item: ProductResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setTitle } = useBreadcrumb();
  const [isEditing, setIsEditing] = useState(false);
  const { apiRequest } = useApiRequest();

  const toggleArchive = async () => {
    await apiRequest(`settings/products/${item._id}/${item.isArchive ? "unarchive" : "archive"}`, "PATCH");
    setReload((prev) => !prev);
  };

  useEffect(() => {
    setTitle(item.title);
  }, [item, setTitle]);

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>Details {item.title}</PageTitle>
          <div className="flex gap-2">
            <ConfirmationDialog
              onConfirm={toggleArchive}
              title="Produkt archivieren"
              description="Möchten Sie das Produkt wirklich archivieren?"
              variant={item.isArchive ? "default" : "destructive"}
            >
              <Button className="gap-2" variant={item.isArchive ? "default" : "destructive"}>
                <span>{item.isArchive ? "Wiederherstellen" : "Archivieren"}</span>
                {item.isArchive ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              </Button>
            </ConfirmationDialog>
            <Button onClick={() => setIsEditing(!isEditing)} className="gap-2" variant="secondary">
              <span>{isEditing ? "Abbrechen" : "Bearbeiten"}</span>
              {isEditing ? <X size={16} /> : <Edit2 size={16} />}
            </Button>
          </div>
        </div>
      </PageTitleContainer>
      <div>
        {isEditing ? (
          <div className="p-4 w-1/2 items-center m-auto">
            <ProductEditor
              item={item}
              onSubmitDone={() => {
                setIsEditing(false);
                setReload((prev) => !prev);
              }}
            />
          </div>
        ) : (
          <div className="w-1/2 m-auto mt-8">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Produkt ID</TableCell>
                  <TableCell>{item.productId}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Titel</TableCell>
                  <TableCell>{item.title}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Beschreibung</TableCell>
                  <TableCell className="whitespace-pre-wrap">{item.description}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Materialkosten</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-bold">{formatPrice(item.netPrice)}</span>
                      <span className="text-sm text-muted-foreground italic">{formatPrice(item.grossPrice)}</span>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Montagekosten</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-bold">{formatPrice(item.netInstallationCost)}</span>
                      <span className="text-sm text-muted-foreground italic">{formatPrice(item.grossInstallationCost)}</span>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Gesamtpreis</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-bold">{formatPrice(item.totalNetPrice)}</span>
                      <span className="text-sm text-muted-foreground italic">{formatPrice(item.totalGrossPrice)}</span>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Provision nach Vertriebsrang (Grundabsicherung)</TableCell>
                  <TableCell>{item.isBasicSecurity ? "Ja" : "Nein"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Erstellt</TableCell>
                  <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Zuletzt Aktualisiert</TableCell>
                  <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductItem;
